import React from 'react';
import {
  UserIcon,
  UsersIcon,
  ClipboardDocumentCheckIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';
import './KPICards.styles.css';

interface KPICard {
  id: 'mine' | 'team' | 'created' | 'unassigned';
  label: string;
  value: number;
  unreadCount?: number;
  icon: React.ReactNode;
  showForRoles?: string[];
}

interface KPICardsProps {
  activeView: 'all' | 'mine' | 'team' | 'created' | 'unassigned';
  stats: {
    all: number;
    mine: number;
    team: number;
    created: number;
    unassigned: number;
  };
  unreadCounts?: {
    all: number;
    mine: number;
    team: number;
    created: number;
    unassigned: number;
  };
  onViewChange: (view: 'mine' | 'team' | 'created' | 'unassigned') => void;
  isLoading?: boolean;
  userRole?: string;
}

export const KPICards: React.FC<KPICardsProps> = ({
  activeView,
  stats,
  unreadCounts,
  onViewChange,
  isLoading = false,
  userRole
}) => {
  // Format number with French locale (thousands separator)
  const formatNumber = (num: number): string => {
    return num.toLocaleString('fr-BE');
  };

  const cards: KPICard[] = [
    {
      id: 'unassigned',
      label: 'À ASSIGNER',
      value: stats.unassigned,
      icon: <ClipboardDocumentCheckIcon className="mes-taches-kpi-icon" aria-hidden="true" />,
      showForRoles: ['admin', 'bureau']
    },
    {
      id: 'mine',
      label: 'MES TÂCHES',
      value: stats.mine,
      unreadCount: unreadCounts?.mine,
      icon: <UserIcon className="mes-taches-kpi-icon" aria-hidden="true" />
    },
    {
      id: 'team',
      label: 'TÂCHES ÉQUIPE',
      value: stats.team,
      unreadCount: unreadCounts?.team,
      icon: <UsersIcon className="mes-taches-kpi-icon" aria-hidden="true" />
    },
    {
      id: 'created',
      label: 'TÂCHES CRÉÉES',
      value: stats.created,
      unreadCount: unreadCounts?.created,
      icon: <PlusCircleIcon className="mes-taches-kpi-icon" aria-hidden="true" />
    }
  ];

  // Filter cards based on user role
  const visibleCards = cards.filter(card => {
    if (card.showForRoles && userRole) {
      return card.showForRoles.includes(userRole);
    }
    return !card.showForRoles; // Show cards without role restrictions to everyone
  });

  return (
    <div className="mes-taches-kpi-container" role="radiogroup" aria-label="Filtres de vue">
      <div className="mes-taches-kpi-scroll">
        {visibleCards.map((card) => {
          const isActive = activeView === card.id;
          
          return (
            <div key={card.id} className="mes-taches-kpi-card-container">
              {/* Notification badge outside the card */}
              {card.unreadCount && card.unreadCount > 0 ? (
                <div className="mes-taches-kpi-notification-badge" title="Messages non lus">
                  <span className="mes-taches-kpi-notification-count">{card.unreadCount}</span>
                </div>
              ) : null}

              <button
                className={`mes-taches-kpi-card ${isActive ? 'active' : ''} compact`}
                onClick={() => onViewChange(card.id)}
                role="radio"
                aria-pressed={isActive}
                aria-checked={isActive}
                aria-label={`${card.label}: ${formatNumber(card.value)} tâches`}
                data-active={isActive}
              >
                {/* Left accent bar for active state */}
                {isActive && (
                  <span className="mes-taches-kpi-accent" aria-hidden="true" />
                )}

                {/* Grid layout for icon and text */}
                <div className="mes-taches-kpi-grid">
                  {card.icon}

                  {isLoading ? (
                    <div className="mes-taches-kpi-skeleton">
                      <div className="mes-taches-skeleton-line mes-taches-skeleton-count" />
                      <div className="mes-taches-skeleton-line mes-taches-skeleton-label" />
                    </div>
                  ) : (
                    // Always use compact mode
                    <div className="mes-taches-kpi-compact">
                      <span className="mes-taches-kpi-value">{formatNumber(card.value)}</span>
                      <span className="mes-taches-kpi-separator">·</span>
                      <span className="mes-taches-kpi-label">{card.label}</span>
                    </div>
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};